import {
  mergeAll as BaconMergeAll,
  when as BaconWhen,
} from 'baconjs';

import {
  always,
  merge,
  prop,
  propEq,
} from 'rambda';

import actionDispatcher from '@/engine/actions/action-dispatcher';
import Api from '@/engine/utils/api';
import notificationStore from '@/engine/stores/notification-store';
import adminTemplateItemDataStore from '@/data/templates/admin-template-item-data-store';

const nameUpdateAction = actionDispatcher.subscribe('template-item-name');
const quantityUpdateAction = actionDispatcher.subscribe('template-item-quantity');
const budgetUpdateAction = actionDispatcher.subscribe('template-item-budget');
const spentUpdateAction = actionDispatcher.subscribe('template-item-spent');
const quantityAddAction = actionDispatcher.subscribe('template-item-quantity-add');
const quantityRemoveAction = actionDispatcher.subscribe('template-item-quantity-remove');
const itemSuggestedAction = actionDispatcher.subscribe('template-item-suggested');

const toNameChange = ({ templateId, itemId, value }) => ({
  data: {
    name: value,
  },
  templateId,
  id: itemId,
  valid: !!value,
});

const toTemplateItemStateChange = ({ templateId, itemId, status }) => ({
  data: {
    status,
  },
  templateId,
  id: itemId,
  valid: !!status,
});

const toQuantityChange = (
  { items },
  {
    templateId,
    itemId,
    value,
    operation,
  },
) => {
  const item = items.find(propEq('id', itemId));
  if (!item) {
    return {
      valid: false,
    };
  }

  let quantity = 0;
  switch (operation) {
    case 'add':
      quantity = item.quantity + 1;
      break;
    case 'remove':
      quantity = item.quantity - 1;
      break;
    default:
      quantity = parseInt(value, 10);
      break;
  }

  let { budget } = item;
  if (quantity > 0) {
    budget = parseInt((item.budget / item.quantity) * quantity, 10);
  }

  return {
    data: {
      quantity,
      budget,
    },
    templateId,
    id: itemId,
    valid: quantity >= 1,
  };
};

const toBudgetChange = ({ templateId, itemId, value }) => ({
  data: {
    budget: value,
  },
  templateId,
  id: itemId,
  valid: true,
});

const toSpentChange = ({ templateId, itemId, value }) => ({
  data: {
    spent: value,
  },
  templateId,
  id: itemId,
  valid: true,
});

const toEditRequest = ({ id, data }) => {
  return Api.put(`/api/admin/template-items/${id}`, data);
};

const toDsChange = ({ id, data }) => {
  return merge({ id }, data);
};

const nameUpdates = nameUpdateAction
  .filter(propEq('event', 'blur'))
  .map(toNameChange);

const itemStatusUpdates = itemSuggestedAction
  .map(toTemplateItemStateChange);

const allQuantityUpdates = BaconMergeAll(
  quantityUpdateAction.map(merge({ operation: 'fixed' })),
  quantityAddAction.map(merge({ operation: 'add' })),
  quantityRemoveAction.map(merge({ operation: 'remove' })),
)
  .toEventStream();

const quantityUpdates = BaconWhen(
  [adminTemplateItemDataStore.updates, allQuantityUpdates],
  toQuantityChange,
);

const budgetUpdates = budgetUpdateAction
  .filter(propEq('event', 'blur'))
  .map(toBudgetChange);

const spentUpdates = spentUpdateAction
  .filter(propEq('event', 'blur'))
  .map(toSpentChange);

const allUpdates = BaconMergeAll(
  nameUpdates,
  quantityUpdates,
  budgetUpdates,
  spentUpdates,
  itemStatusUpdates,
)
  .filter(prop('valid'));

const dsChanges = allUpdates
  .map(toDsChange);

const updateRequests = allUpdates
  .flatMap(toEditRequest);

const failedUpdateRequests = updateRequests
  .errors()
  .mapError(always({ message: 'Failed to save change', type: 'error' }));

const failedUpdatesUnsubscribe = failedUpdateRequests.onValue(notificationStore.publish);
const updateDataStoreUnsubscribe = dsChanges.onValue(adminTemplateItemDataStore.update);

if (process.env.NODE_ENV === 'development') {
  if (module.hot) {
    module.hot.accept();
    module.hot.dispose(() => {
      failedUpdatesUnsubscribe();
      updateDataStoreUnsubscribe();
    });
  }
}
